import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { designTokens } from '@/utils/design-tokens'
import type { OrgMemberResponse } from '@/api/generated/models/OrgMemberResponse'
import { useAlert } from '@/components/ui/Alert/exports'

interface MemberEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (memberData: {
    role?: string
    available: boolean
  }) => void
  member: OrgMemberResponse | null
  isLoading?: boolean
  error?: string | null
}

export default function MemberEditModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  member,
  isLoading = false,
  error = null
}: MemberEditModalProps) {
  const { showError } = useAlert()
  const [accountName, setAccountName] = useState('')
  const [accountEmail, setAccountEmail] = useState('')
  const [accountPhone, setAccountPhone] = useState('')
  const [role, setRole] = useState('member')
  const [available, setAvailable] = useState(true)

  // Pre-populate form with existing member data
  useEffect(() => {
    if (member) {
      setAccountName(member.account_name || '')
      setAccountEmail(member.account_email || '')
      setAccountPhone(member.account_phone || '')
      setRole(member.role || 'member')
      setAvailable(member.available ?? true)
    }
  }, [member, member?.id])

  // Show error alert when error prop changes
  useEffect(() => {
    if (error) {
      showError(error)
    }
  }, [error, showError])

  const handleSubmit = () => {
    onSubmit({
      role: role || 'member',
      available
    })
  }

  const handleClose = () => {
    setAccountName('')
    setAccountEmail('')
    setAccountPhone('')
    setRole('member')
    setAvailable(true)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Background Blur Overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        onClick={handleClose}
      />
      
      {/* Modal Content */}
      <div 
        className="relative bg-white rounded w-[600px] max-w-[90vw] max-h-[90vh] overflow-auto border"
        style={{ 
          borderColor: designTokens.colors.gray[1]
        }}
      >
        <div className="flex flex-col gap-4 p-[30px]">
          {/* Header */}
          <div className="flex items-center justify-between gap-[10px] py-[7px]">
            <h2 
              className="font-bold"
              style={{ 
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-medium'][0],
                lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
                color: designTokens.colors.gray[6]
              }}
            >
              멤버 수정
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
              style={{ color: designTokens.colors.gray[5] }}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>


          {/* Content */}
          <div className="flex flex-col gap-6">
            {/* Form Section */}
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-[10px]">
                {/* Member Name Input */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label 
                        style={{ 
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        이름
                        <span 
                          className="ml-1"
                          style={{ 
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                            lineHeight: designTokens.typography.fontSize['body-xsmall'][1].lineHeight,
                            color: designTokens.colors.error.DEFAULT
                          }}
                        >
                          *
                        </span>
                      </label>
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="멤버 이름을 입력해주세요."
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        size="small"
                        className="w-full"
                        disabled
                        autoFocus
                      />
                    </div>
                  </div>
                </div>

                {/* Email Input */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label 
                        style={{ 
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        이메일
                        <span 
                          className="ml-1"
                          style={{ 
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                            lineHeight: designTokens.typography.fontSize['body-xsmall'][1].lineHeight,
                            color: designTokens.colors.error.DEFAULT
                          }}
                        >
                          *
                        </span>
                      </label>
                    </div>
                    <div className="flex-1">
                      <Input
                        type="email"
                        placeholder="이메일 주소를 입력해주세요."
                        value={accountEmail}
                        onChange={(e) => setAccountEmail(e.target.value)}
                        size="small"
                        className="w-full"
                        disabled
                      />
                    </div>
                  </div>
                </div>

                {/* Phone Input */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label 
                        style={{ 
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        전화번호
                      </label>
                    </div>
                    <div className="flex-1">
                      <Input
                        type="tel"
                        placeholder="전화번호를 입력해주세요. (선택사항)"
                        value={accountPhone}
                        onChange={(e) => setAccountPhone(e.target.value)}
                        size="small"
                        className="w-full"
                        disabled
                      />
                    </div>
                  </div>
                </div>

                {/* Role Input */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label 
                        style={{ 
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        역할
                      </label>
                    </div>
                    <div className="flex-1">
                      <select
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="w-full px-3 py-0 h-10 border-2 rounded cursor-pointer transition-all duration-150"
                        style={{
                          backgroundColor: designTokens.colors.white,
                          borderColor: designTokens.colors.gray[1],
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        <option value="normal">일반</option>
                        <option value="admin">관리자</option>
                        <option value="owner">소유자</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Status Toggle */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label 
                        style={{ 
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        상태
                        <span 
                          className="ml-1"
                          style={{ 
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                            lineHeight: designTokens.typography.fontSize['body-xsmall'][1].lineHeight,
                            color: designTokens.colors.error.DEFAULT
                          }}
                        >
                          *
                        </span>
                      </label>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="status"
                            checked={available}
                            onChange={() => setAvailable(true)}
                            className="w-4 h-4"
                            style={{ accentColor: designTokens.colors.primary.DEFAULT }}
                          />
                          <span 
                            style={{ 
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: designTokens.typography.fontSize['body-medium'][0],
                              lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                              fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                              color: designTokens.colors.gray[6]
                            }}
                          >
                            활성
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="status"
                            checked={!available}
                            onChange={() => setAvailable(false)}
                            className="w-4 h-4"
                            style={{ accentColor: designTokens.colors.primary.DEFAULT }}
                          />
                          <span 
                            style={{ 
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: designTokens.typography.fontSize['body-medium'][0],
                              lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                              fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                              color: designTokens.colors.gray[6]
                            }}
                          >
                            비활성
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-[10px]">
              <Button
                color="light"
                size="medium"
                onClick={handleClose}
                disabled={isLoading}
                className="px-[10px] h-10"
              >
                취소
              </Button>
              <Button
                color="dark" 
                size="medium"
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-[10px] h-10"
              >
                {isLoading ? '저장 중...' : '저장'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}